import styled, { keyframes } from "styled-components";

const sbFade = keyframes`
  from { opacity: 0; transform: translateX(6px); }
  to   { opacity: 1; transform: translateX(0); }
`;

export const BannerRoot = styled.div`
  --sg-navy: #314f71;
  --sg-accent: #dc3545;
  --sg-accent-pink: #f45b68;
  --sg-success: #52c41a;
  --sg-muted-icon: #8c8c8c;
  --sg-nav-disabled: #d9d9d9;
  --sg-cta-disabled-bg: var(--app-surface-alt);
  --sg-cta-disabled-border: var(--app-border);
  --sg-cta-disabled-text: var(--app-text-muted);
  --sg-active-step-bg: color-mix(in srgb, #dc3545 8%, var(--app-surface));

  width: 100%;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
  overflow: hidden;
  color: var(--app-text);

  .desktop-layout {
    display: flex;
    flex-direction: column;
  }

  .setup-guide-row {
    display: flex;
    align-items: center;
    gap: 0;
    min-height: 64px;
  }

  .setup-guide-rail {
    background: var(--sg-navy);
    padding: 12px 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-self: stretch;
    min-width: 140px;
    flex-shrink: 0;
  }

  .setup-guide-rail-title {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 6px;
    color: #fff;
    font-weight: 700;
    font-size: 12px;
    white-space: nowrap;
  }

  .setup-guide-rail-title i {
    font-size: 14px;
    color: #fff;
  }

  .setup-guide-dots {
    display: flex;
    gap: 4px;
  }

  .setup-guide-dot {
    height: 6px;
    border-radius: 3px;
    border: none;
    padding: 0;
    flex-shrink: 0;
    transition: all 0.25s ease;
  }

  .setup-guide-dot--active {
    width: 18px;
  }

  .setup-guide-dot--idle {
    width: 8px;
  }

  .setup-guide-dot--done {
    background: var(--sg-success);
    cursor: pointer;
  }

  .setup-guide-dot--active-color {
    background: var(--sg-accent-pink);
    cursor: pointer;
  }

  .setup-guide-dot--idle-color {
    background: rgba(255, 255, 255, 0.25);
    cursor: pointer;
  }

  .setup-guide-dot--locked {
    cursor: not-allowed;
    opacity: 0.4;
  }

  .setup-guide-rail-meta {
    color: rgba(255, 255, 255, 0.45);
    font-size: 10px;
    margin-top: 5px;
    white-space: nowrap;
  }

  .setup-guide-divider-v {
    width: 1px;
    align-self: stretch;
    background: var(--app-border);
    flex-shrink: 0;
  }

  .setup-guide-content {
    flex: 1;
    min-width: 0;
    padding: 12px 20px;
    animation: ${sbFade} 0.2s ease;
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .setup-guide-step-icon {
    font-size: 22px;
    flex-shrink: 0;
    display: inline-flex;
  }

  .setup-guide-step-icon i {
    font-size: 22px;
  }

  .setup-guide-step-icon--done i {
    color: var(--sg-success);
  }

  .setup-guide-step-icon--skipped i {
    color: var(--sg-muted-icon);
  }

  .setup-guide-step-icon--default i {
    color: var(--sg-navy);
  }

  .setup-guide-step-icon--locked {
    opacity: 0.3;
  }

  .setup-guide-text {
    min-width: 0;
    flex: 1;
  }

  .setup-guide-label-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 2px;
    flex-wrap: wrap;
  }

  .setup-guide-step-label {
    font-size: 10px;
    font-weight: 700;
    color: var(--sg-accent);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }

  .setup-guide-badge-mandatory {
    font-size: 9px;
    background: var(--sg-accent);
    color: #fff;
    padding: 1px 5px;
    border-radius: 3px;
    font-weight: 700;
    white-space: nowrap;
  }

  .setup-guide-badge-status {
    font-size: 9px;
    color: #fff;
    padding: 1px 6px;
    border-radius: 10px;
    font-weight: 700;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .setup-guide-badge-status--done {
    background: var(--sg-success);
  }

  .setup-guide-badge-status--skipped {
    background: var(--sg-muted-icon);
  }

  .setup-guide-step-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--app-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .setup-guide-step-title--locked {
    color: var(--app-text-muted);
  }

  .setup-guide-step-desc {
    font-size: 11px;
    color: var(--app-text-muted);
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .setup-guide-step-desc--locked {
    opacity: 0.7;
  }

  .setup-guide-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    flex-shrink: 0;
  }

  .setup-guide-cta {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 14px;
    border-radius: 6px;
    background: var(--sg-accent);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    box-shadow: 0 2px 6px rgba(220, 53, 69, 0.25);
    transition: opacity 0.15s;
  }

  .setup-guide-cta:hover {
    opacity: 0.85;
    color: #fff;
  }

  .setup-guide-cta--disabled {
    background: var(--sg-cta-disabled-bg);
    color: var(--sg-cta-disabled-text);
    border: 1px solid var(--sg-cta-disabled-border);
    cursor: not-allowed;
    box-shadow: none;
  }

  .setup-guide-dismiss {
    background: none;
    border: none;
    padding: 0;
    font-size: 11px;
    color: var(--app-text-muted);
    cursor: pointer;
    text-decoration: underline;
    font-family: inherit;
    white-space: nowrap;
  }

  .setup-guide-divider-h {
    width: 1px;
    height: 28px;
    background: var(--app-border);
    flex-shrink: 0;
  }

  .setup-guide-nav-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    flex-shrink: 0;
    transition: background 0.15s;
  }

  .setup-guide-nav-btn i {
    font-size: 20px;
    color: #fff;
  }

  .setup-guide-nav-btn--enabled {
    background: var(--sg-navy);
    cursor: pointer;
  }

  .setup-guide-nav-btn--disabled {
    background: var(--sg-nav-disabled);
    cursor: not-allowed;
  }

  .setup-guide-progress-track {
    height: 3px;
    background: var(--app-surface-alt);
  }

  .setup-guide-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--sg-accent-pink), var(--sg-accent));
    transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Mobile */
  .mobile-layout {
    display: none;
  }

  .setup-guide-mobile-header {
    background: var(--sg-navy);
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .setup-guide-mobile-toggle {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 6px;
    padding: 6px 12px;
    color: #fff;
    font-size: 12px;
    cursor: pointer;
  }

  .setup-guide-mobile-body {
    padding: 16px;
  }

  .setup-guide-mobile-step-icon {
    font-size: 28px;
    flex-shrink: 0;
    display: inline-flex;
  }

  .setup-guide-mobile-step-icon i {
    font-size: 28px;
  }

  .setup-guide-mobile-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--app-text);
    margin-bottom: 4px;
  }

  .setup-guide-mobile-desc {
    font-size: 13px;
    color: var(--app-text-muted);
    line-height: 1.4;
  }

  .setup-guide-mobile-cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 6px;
    background: var(--sg-accent);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    text-align: center;
    width: 100%;
  }

  .setup-guide-mobile-cta--disabled {
    background: var(--sg-cta-disabled-bg);
    color: var(--sg-cta-disabled-text);
    border: 1px solid var(--sg-cta-disabled-border);
    cursor: not-allowed;
  }

  .setup-guide-mobile-nav {
    flex: 1;
    padding: 8px 16px;
    border-radius: 6px;
    border: none;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
  }

  .setup-guide-mobile-nav--enabled {
    background: var(--sg-navy);
    cursor: pointer;
  }

  .setup-guide-mobile-nav--disabled {
    background: var(--sg-nav-disabled);
    cursor: not-allowed;
  }

  .mobile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }

  .mobile-steps-menu {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: var(--app-surface);
    border-top: 1px solid var(--app-border);
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.12);
    transform: translateY(100%);
    transition: transform 0.3s ease;
    z-index: 1000;
  }

  .mobile-steps-menu.open {
    transform: translateY(0);
  }

  .mobile-steps-menu-title {
    font-weight: 700;
    margin-bottom: 12px;
    font-size: 14px;
    color: var(--app-text);
  }

  .setup-guide-mobile-step-btn {
    width: 100%;
    text-align: left;
    padding: 12px;
    margin-bottom: 8px;
    border-radius: 8px;
    border: 1px solid var(--app-border);
    background: var(--app-surface);
    cursor: pointer;
  }

  .setup-guide-mobile-step-btn--active {
    border: 2px solid var(--sg-accent);
    background: var(--sg-active-step-bg);
  }

  .setup-guide-mobile-step-btn--locked {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .setup-guide-mobile-step-btn-title {
    font-weight: 700;
    font-size: 13px;
    color: var(--app-text);
  }

  .setup-guide-mobile-step-btn-label {
    font-size: 11px;
    color: var(--app-text-muted);
  }

  @media (max-width: 768px) {
    .desktop-layout {
      display: none !important;
    }
    .mobile-layout {
      display: block !important;
    }
    .mobile-steps-menu {
      display: block;
    }
  }

  @media (min-width: 769px) {
    .desktop-layout {
      display: flex !important;
    }
    .mobile-layout {
      display: none !important;
    }
  }

  [data-bs-theme="dark"] & {
    --sg-navy: #3d5a7a;
    --sg-nav-disabled: #3f4254;
    --sg-muted-icon: #a1a5b7;
    --sg-active-step-bg: color-mix(in srgb, #f45b68 14%, var(--app-surface));

    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.28);
  }
`;
